import React, { FC } from 'react'

import { useGetStashedStorageItems } from '../../operations'

import {
  useForTablet,
  DocumentHead,
  ErrorMessage,
  DocumentHeader,
  LoadingState,
} from '@sketch/components'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { LayoutPortal, useSearch } from '@sketch/utils'

import { SKETCH_WEBSITE } from '@sketch/env-config'
import { workspaceSidebarLayoutSymbols } from '../../../workspace/components/WorkspaceSidebarLayout'
import { useFolderTerm } from '@sketch/modules-common'

import TrashListEmptyState from './TrashListEmptyState'
import TrashItemsLayout from './TrashItemsLayout'

export interface TrashListProps {
  workspaceId: string
  userRole: WorkspaceMinimalFragment['userRole']
}

export const TrashList: FC<TrashListProps> = ({ workspaceId, userRole }) => {
  const { searchDebounced: search } = useSearch()
  const isTabletAndBigger = useForTablet()

  const result = useGetStashedStorageItems({
    workspaceId,
    search,
    source: 'TRASH',
  })
  const folderTerm = useFolderTerm()

  if (result.kind === 'loading') return <LoadingState />
  if (!result || result.kind === 'error') return <ErrorMessage.Generic />

  const { fetchMore, items, loading, totalCounts } = result

  const header = isTabletAndBigger && (
    <>
      <LayoutPortal token={workspaceSidebarLayoutSymbols.header}>
        <DocumentHeader
          title="Trash"
          learnMoreTooltipTitle="Learn about deleting documents"
          learnMoreTooltipURL={`${SKETCH_WEBSITE}/docs/getting-started/saving-and-managing-documents/#deleting-documents`}
          description={`Documents and ${folderTerm.lower}s in Trash are permanently deleted after 90 days.`}
        />
      </LayoutPortal>
      <LayoutPortal token={workspaceSidebarLayoutSymbols.headerSlim}>
        <DocumentHeader title="Trash" />
      </LayoutPortal>
    </>
  )

  if (!loading && items.length === 0) {
    return (
      <>
        {header}
        <TrashListEmptyState search={search} />
      </>
    )
  }

  return (
    <>
      <DocumentHead title="Trash" />
      <TrashItemsLayout
        items={items}
        loading={loading}
        userRole={userRole}
        totalItemsCount={totalCounts.storageItems}
        onLoadMore={fetchMore}
        workspaceId={workspaceId}
        header={header}
      />
    </>
  )
}
